import {getManager} from "./Manager";
import {isPlatform} from "@ionic/react";
import {IAnyObject} from "../common/SimpleInterface";
import {isArray, isNativeObject, parseBool, trimSlashes} from "../common/SiteHelper";
import {resetModeFactoryModelsPopulated} from "../model-factory/helper/ModelFactoryHelper";
import {dispatchSiteLoad} from "../event/DispatchEvent";
import {getModelFactory} from "../model-factory/ModelFactory";


class AppSocialChannel
{
    link:string = "";
    faIconID:string = "";
    faIconClassName:string = "";
    valid = false;

    constructor(fromValue:any)
    {
        if (!isNativeObject(fromValue))
        {
            return;
        }
        this.link = fromValue.link ?? "";
        const faIcon = isNativeObject(fromValue.fa_icon) ? fromValue.fa_icon : {};
        this.faIconID = faIcon.id ?? "";
        this.faIconClassName = faIcon.class ?? "";
        this.valid = !!this.link && !!this.faIconID;
    }
}

class AppAddressDetails
{
    name: string = "";
    address: string = "";
    address2: string = "";
    town: string = "";
    county: string = "";
    postcode: string = "";

    applyDetails(fromValue:any)
    {
        if(!isNativeObject(fromValue))
        {
        }
        this.name = fromValue.name ?? "";
        this.address = fromValue.address ?? "";
        this.address2 = fromValue.address_2 ?? "";
        this.town = fromValue.town ?? "";
        this.county = fromValue.county ?? "";
        this.postcode = fromValue.postcode ?? "";
    }
}

class AppInfo
{
    title: string = "";
    emailAddress:string = "";
    telephoneNumber:string = "";
    mobileNumber:string = "";
    faxNumber:string = "";
    addressDetails:AppAddressDetails = new AppAddressDetails;
    socialChannels:AppSocialChannel[] = [];

    applyDetails(fromValue:any)
    {
        if(!isNativeObject(fromValue))
        {
            return;
        }
        this.title = fromValue.title ?? "";
        this.emailAddress = fromValue.email_address ?? "";
        this.telephoneNumber = fromValue.telephone_number ?? "";
        this.mobileNumber = fromValue.mobile_number ?? "";
        this.faxNumber = fromValue.fax_number ?? "";
        this.addressDetails .applyDetails(fromValue.address_details);
        const fromSocialChannels = isArray(fromValue.social_channels) ? fromValue.social_channels : [];
        this.socialChannels = [];
        for(let fromSocialChannel of fromSocialChannels){
            const socialChannel = new AppSocialChannel(fromSocialChannel);
            if(socialChannel.valid){
                this.socialChannels.push(socialChannel);
            }
        }
    }
}

export default class AppManager
{
    appDebug:boolean = false;
    processedJS:boolean = false;
    noServerAvailable:boolean = false;
    resetAppData:boolean = false;
    site:AppInfo = new AppInfo();
    appHome:string = ""

    constructor()
    {

    }

    init(): void
    {
        this.parseEnv();
    }

    parseEnv(): void {

        // @ts-ignore
        let env = typeof process !== "undefined" && process ? process.env : null;
        if(!env){
            // @ts-ignore
            env = import.meta.env;
        }

        env = env ? env : {};

        this.processedJS = parseBool(env["PROCESSED_JS"]);

        const isMobile: boolean = !isPlatform("desktop") && !isPlatform("mobileweb");
        //this.noServerAvailable = this.processedJS || isMobile;
        const apiHomeKey = (this.processedJS || isMobile) ? "VITE_API_HOME" : "VITE_API_LOCAL_HOME";
        let googleAddressEndpoint = trimSlashes(env["VITE_GOOGLE_ADDRESS_ENDPOINT"] ?? "");
        this.appHome = trimSlashes(env["VITE_APP_HOME"] ?? "");

        getManager().api.apiHome = trimSlashes((env[apiHomeKey] ?? ""));
        getManager().api.connectionTestImage = env["VITE_CONNECTION_TEST_IMAGE"] ?? "";
        googleAddressEndpoint = googleAddressEndpoint ? (!(googleAddressEndpoint.startsWith("https://") || googleAddressEndpoint.startsWith("http://")) ? getManager().api.apiHome + "/" + googleAddressEndpoint : googleAddressEndpoint) : "";
        getManager().location.googleAddressEndpoint = googleAddressEndpoint;
        this.appDebug = parseBool(env["VITE_APP_DEBUG"]);
    }


    async requireAppLoadData(): Promise<string|null>
    {
        const includeConfig = () => {
            return !getManager().config.initialized;
        }

        const includeUser = () => {
            return getManager().user.doesRequireLogin();
        }

        const loadRequired = () => {
            return includeConfig() || includeUser() || this.resetAppData;
        }
        if(!loadRequired()){
            return null;
        }

        return getManager().config.getConfigVersion().then(configVersion => {
            if(!loadRequired()){
                return null;
            }
            const params:IAnyObject = {};
            if(includeConfig() || this.resetAppData){
                params.include_config = true;
                //params.config_version = configVersion;
            }
            if(includeUser() || this.resetAppData){
                params.include_user = true;
            }

            const endpoint = "/app/app_load";

            return getManager().api.fetchData(endpoint, "GET", params, includeUser()).then(responseVO => {
                this.resetAppData = false;

                if(responseVO.apiError){
                    if(responseVO.hasNoInternetConnection){
                        return getManager().config.applyLocalConfig().then(applyConfigError => {
                            if(applyConfigError){
                                return applyConfigError;
                            }

                            if(includeUser() && !getManager().user.loggedInLocal){
                                const cachedUserApplied = getManager().user.applyCachedUser();
                                if(!cachedUserApplied){
                                   getManager().user.logOut();
                                    return null;
                                }
                            }
                            return getManager().storageData.populateFromStorage().then(_ => {
                                return null;
                            });

                        })
                    } else {
                        if(includeUser()){
                            delete params.include_user;
                            return getManager().api.fetchData(endpoint, "GET", params).then(responseVO => {
                                if(responseVO.apiError){
                                    return responseVO.apiError.message ? responseVO.apiError.message : "Unknown Error [1]";
                                }

                                return getManager().config.applyConfigResponse(responseVO.data.config).then(applyConfigError => {
                                    if(applyConfigError){
                                        return applyConfigError;
                                    }
                                    getManager().user.logOut();
                                    return null;
                                });
                            })
                        }
                        return responseVO.apiError.message ? responseVO.apiError.message : "Unknown Error [2]";
                    }
                } else {
                    getManager().storageData.requirePreviousStorage().then(_ => {
                        getManager().storageData.clearStorage();
                        getManager().storage.clear("file").then(_ => {});
                    })
                }


                return getManager().config.applyConfigResponse(responseVO.data.config).then(applyConfigError => {
                    if(applyConfigError){
                        return applyConfigError;
                    }
                    if(includeUser()){

                        let error:string|null = getManager().user.setUser(responseVO.data.user);
                        if(error){
                            getManager().user.logOut();
                        } else {
                            getManager().user.handleUserLogInOut();
                        }
                    }

                    return null;
                });
            });
        }).then(async (message) =>
        {
            const pageModel = getModelFactory().pageData.getPageDataModel();
            const apiError = await pageModel.requirePopulated();
            if (apiError && !message)
            {
                message = apiError.message;
            }
            dispatchSiteLoad();
            return message;
        });
    }

    async handleRefresh(): Promise<boolean> {
        this.resetAppData = true;
        resetModeFactoryModelsPopulated();
        return getManager().navigation.applyCurrentRoute(true);
    }
}